/* QUICK LISTS - 2D Grid Workspace with Atomic Reordering & Smooth Layout Transitions */
import React, { useState, useMemo, useEffect } from 'react';
import { useStore } from '../store/StoreContext';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { Button } from '../components/UI/Button';
import { generateId } from '../utils/helpers';

const NotepadCard = ({ list, onUpdate, onDelete, onMove, index, isLast }) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState(list.title);
  const [newItem, setNewItem] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');

  const displayItems = useMemo(() => {
    const rawItems = (list.items || []).map((item, idx) => 
      typeof item === 'string' ? { id: `item-${idx}`, text: item, completed: false } : { id: item.id || `item-${idx}`, ...item }
    );
    return [...rawItems].sort((a, b) => (a.completed === b.completed ? 0 : a.completed ? 1 : -1));
  }, [list.items]);

  const handleTitleBlur = () => {
    setEditingTitle(false);
    if (title !== list.title) onUpdate({ ...list, title: title || 'Untitled List' });
  };

  const addItem = (e) => {
    e.preventDefault();
    if (!newItem.trim()) return;
    onUpdate({ ...list, items: [...(list.items || []), { id: generateId('li'), text: newItem.trim(), completed: false }] });
    setNewItem('');
  };

  const toggleItem = (id) => {
    const updated = (list.items || []).map(item => item.id === id ? { ...item, completed: !item.completed } : item);
    onUpdate({ ...list, items: updated });
  };

  const removeItem = (id) => {
    onUpdate({ ...list, items: (list.items || []).filter(item => item.id !== id) });
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="relative w-full min-h-[420px] bg-[#fef9c3] rounded-lg shadow-xl overflow-hidden flex flex-col group border border-yellow-200/50"
      style={{
        backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px)',
        backgroundSize: '100% 1.4rem',
        paddingTop: '1.4rem'
      }}
    >
      <div className="absolute left-10 top-0 bottom-0 w-[1.5px] bg-red-200/60 z-0" />
      
      {/* Card Controls */}
      <div className="absolute right-3 top-3 flex gap-1 z-30 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onMove(index, -1)} disabled={index === 0} className="p-1.5 bg-white/80 hover:bg-white rounded-md text-gray-500 disabled:opacity-20">
          <SafeIcon name="ArrowLeft" className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onMove(index, 1)} disabled={isLast} className="p-1.5 bg-white/80 hover:bg-white rounded-md text-gray-500 disabled:opacity-20">
          <SafeIcon name="ArrowRight" className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(list.id)} className="p-1.5 bg-red-50 hover:bg-red-500 hover:text-white rounded-md text-red-400 transition-colors">
          <SafeIcon name="Trash2" className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="px-12 pb-1 relative z-10 mb-2">
        {editingTitle ? (
          <input
            autoFocus
            className="bg-transparent border-none outline-none font-black text-2xl text-gray-800 w-full p-0"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={(e) => e.key === 'Enter' && handleTitleBlur()}
          />
        ) : (
          <h3 className="font-black text-2xl text-gray-800 hover:text-blue-600 transition-colors truncate cursor-text" onClick={() => setEditingTitle(true)}>
            {list.title}
          </h3>
        )}
      </div>

      <div className="flex-1 px-12 py-0 relative z-10 overflow-visible">
        <Reorder.Group axis="y" values={displayItems} onReorder={(newOrder) => onUpdate({ ...list, items: newOrder })} className="space-y-0">
          {displayItems.map((item) => (
            <Reorder.Item key={item.id} value={item} className="flex items-center gap-2 group/item h-[1.4rem] relative bg-[#fef9c3]">
              <div className="shrink-0 cursor-ns-resize opacity-0 group-hover/item:opacity-40 hover:!opacity-100 transition-opacity">
                <SafeIcon name="GripVertical" className="w-3 h-3 text-gray-600" />
              </div>
              <button onClick={() => toggleItem(item.id)} className={`shrink-0 z-20 ${item.completed ? 'text-green-500' : 'text-gray-300 hover:text-gray-500'}`}>
                <SafeIcon name={item.completed ? "CheckCircle" : "Circle"} className="w-3.5 h-3.5" />
              </button>
              <div className="flex-1 min-w-0 flex items-center justify-between relative group/pop">
                <span 
                  className={`text-sm font-medium leading-none truncate cursor-text transition-all flex-1 ${item.completed ? 'text-gray-400 line-through' : 'text-gray-700 hover:text-blue-600'}`}
                  onClick={() => { setEditingIndex(item.id); setEditValue(item.text); }}
                >
                  {editingIndex === item.id ? (
                    <input
                      autoFocus
                      className="w-full bg-blue-100/50 border-none outline-none"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => {
                        if (editValue.trim()) {
                          const updated = (list.items || []).map(i => i.id === item.id ? { ...i, text: editValue.trim() } : i);
                          onUpdate({ ...list, items: updated });
                        }
                        setEditingIndex(null);
                      }}
                    />
                  ) : item.text}
                </span>
                <button onClick={() => removeItem(item.id)} className="opacity-0 group-hover/item:opacity-100 text-gray-400 hover:text-red-500 p-0.5 z-20 bg-[#fef9c3]">
                  <SafeIcon name="X" className="w-3 h-3" />
                </button>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </div>

      <div className="px-12 py-4 relative z-10 mt-auto bg-white/40 border-t border-yellow-200 backdrop-blur-sm">
        <form onSubmit={addItem} className="flex gap-3">
          <input
            className="flex-1 bg-transparent border-none outline-none text-sm text-gray-800 placeholder-gray-400 font-bold"
            placeholder="Add quick detail..."
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
          />
          <button type="submit" className="text-blue-600 hover:text-blue-700 transition-all hover:scale-110 active:scale-95">
            <SafeIcon name="PlusCircle" className="w-6 h-6" />
          </button>
        </form>
      </div>
    </motion.div>
  );
};

export const QuickLists = () => {
  const { state, addEntity, updateEntity, deleteEntity } = useStore();
  const [listIds, setListIds] = useState([]);

  useEffect(() => {
    const storeIds = Object.values(state.quickLists || {})
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .map(l => l.id);

    setListIds(prev => {
      if (prev.length !== storeIds.length || storeIds.some(id => !prev.includes(id))) {
        const valid = prev.filter(id => storeIds.includes(id));
        const newOnes = storeIds.filter(id => !prev.includes(id));
        return [...valid, ...newOnes];
      }
      return prev;
    });
  }, [state.quickLists]);

  const sortedLists = listIds.map(id => state.quickLists[id]).filter(Boolean);

  const handleMove = (index, direction) => {
    const newIds = [...listIds];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newIds.length) return;
    [newIds[index], newIds[targetIndex]] = [newIds[targetIndex], newIds[index]];
    setListIds(newIds);
  };

  const handleCreate = async () => {
    const newList = { id: generateId('ql'), title: 'New Quick List', items: [], created_at: new Date().toISOString() };
    await addEntity('quickLists', newList);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 h-full overflow-y-auto bg-base-900 custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-10">
          <div>
            <h1 className="text-3xl font-black text-white tracking-tight flex items-center gap-3">
              <SafeIcon name="List" className="text-primary" /> Quick Lists
            </h1>
            <p className="text-sm text-gray-400 font-medium mt-1 uppercase tracking-widest">2D Grid Reordering • Instant Brainstorming</p>
          </div>
          <Button onClick={handleCreate} className="shadow-2xl h-12 px-6">
            <SafeIcon name="Plus" className="mr-2 w-5 h-5" /> New Notepad
          </Button>
        </div>

        {sortedLists.length === 0 ? (
          <div className="h-[500px] flex flex-col items-center justify-center text-gray-600 bg-base-800/20 rounded-3xl border border-dashed border-base-700">
            <SafeIcon name="FileText" className="w-16 h-16 mb-4 opacity-10" />
            <p className="text-xl font-bold text-gray-500">Your desk is clear.</p>
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 lg:grid-cols-2 gap-10 pb-20">
            <AnimatePresence mode="popLayout">
              {sortedLists.map((list, idx) => (
                <NotepadCard 
                  key={list.id} 
                  list={list} 
                  index={idx}
                  isLast={idx === sortedLists.length - 1}
                  onMove={handleMove}
                  onUpdate={(item) => updateEntity('quickLists', item)}
                  onDelete={(id) => deleteEntity('quickLists', id)}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};